/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { Formik, FieldArray, yupToFormErrors, FormikProps } from 'formik'
import { TextArea } from '@mdsol/lego'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import Modal from '../../../../components/Modal'
import FsiPicker from '../../../../components/FsiPicker/FsiPicker'
import { TreeNode } from '../../../../components/FsiPickerNode/FsiPickerNode'
import { UserGroupSteps } from '../../../../components/workflow/UserGroupSteps'
import { createValidationSchema } from './FormValidationForAddAlias'
import { ErrorMessageStyled as ErrorMessage } from '~/components/shared/ErrorMessage'
import Field from '~/components/shared/Field'
import { Link, useNavigate } from 'react-router'
import { useWorkflowCreation } from '../../../../hooks/useWorkflowCreation'
import { WorkflowType } from '../../../../constants/workflow'
import { formatTextAreaCounter } from '../../../../utils/formHelpers'
import { WorkflowStep } from '../../../../services/api/files'
import { useEsignature } from '~/hooks/useEsignature'
import useWorkflowServiceNew from '../../../../services/workflow/useWorkflowServiceNew'

interface FsiObject {
  id: number
  name?: string
  fileName?: string
  type?: string
  systemFolderType?: string
  templateId?: number
}

interface FileVersion {
  id: number
  versionNo: string
  minorVersionId: number
  effectiveDate: number
  createdBy: {
    id: number
    name: string
  }
  size: number
}

interface User {
  id: number
  firstName?: string
  lastName?: string
}

interface WorkflowStepGroup {
  type: string
  steps: WorkflowStep[]
}

interface FormValues {
  description: string
  fileName: string
  fsi: FsiObject | null
  typeData?: {
    initialApprovers?: WorkflowStepGroup
    approvers?: WorkflowStepGroup
  }
}

interface RequestData {
  approver: User | Record<string, never>
  fileName?: string
  description?: string
  parentFolderName?: string
}

interface AddAliasModalProps {
  parentFsi: FsiObject
  onClose?: () => void
}

export function AddAliasModal({ parentFsi, onClose }: AddAliasModalProps) {
  const context = useServiceContext()
  const { t } = useTranslation()

  const workflowService = useWorkflowServiceNew()
  const isCreatingRef = useRef(false)

  const [isOpen, setIsOpen] = useState(true)
  const [selectedDocument, setSelectedDocument] = useState<FsiObject>({} as FsiObject)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [versions, setVersions] = useState<FileVersion[]>([])
  const [request, setRequest] = useState<RequestData>({
    approver: {}
  })
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    description: '',
    fileName: '',
    fsi: null
  })

  const validationSchema = createValidationSchema()
  const formikRef = useRef<FormikProps<FormValues>>(null)
  const navigate = useNavigate()

  const bytes = (bytes: number) => {
    // Simple byte formatting - could be enhanced
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  useEffect(() => {
    const requestData: RequestData = { ...request }
    if (parentFsi?.id) {
      requestData.parentFolderName = parentFsi?.name
    } else {
      doClose()
      return
    }

    requestData.description = t('CR_DESC_DEFAULT_PLACEHOLDER_ADD_REFERENCE')
    setInitialFormValues((oldData: FormValues) => ({
      ...oldData,
      description: requestData.description || ''
    }))

    const typeData: {
      initialApprovers?: WorkflowStepGroup
      approvers?: WorkflowStepGroup
    } = {}

    Promise.all([
      context.api.files.getFileCRApprovers(parentFsi.id, true),
      context.api.files.getFileApprovers(parentFsi.id)
    ]).then(function (results) {
      typeData.initialApprovers = results[0].initialApprovers as WorkflowStepGroup
      typeData.approvers = results[1].approvers as WorkflowStepGroup
      setInitialFormValues((oldData: FormValues) => ({ ...oldData, typeData: typeData }))
    })

    // Get current user as approver
    const currentUser = context.service.auth.getUser()
    if (currentUser) {
      requestData.approver = currentUser
    }
    setRequest(requestData)
  }, [parentFsi, t, context])

  const documentSelectedFromComponent = (document: FsiObject) => {
    if (document?.type !== 'DOCUMENT') {
      return
    }

    // Use DOM API for focus and select (Field component doesn't support ref)
    setTimeout(function () {
      const input = window.document.getElementById('filenameInput') as HTMLInputElement
      if (input) {
        input.focus()
        input.select()
      }
    }, 20)

    // Fetch file versions and update state
    context.api.files.getFileVersions(document.id).then(function (data) {
      setVersions(data.versions)
      setSelectedVersion(null) // Reset to "Current" option
    })
  }

  const save = async (values: FormValues) => {
    // Prevent multiple submissions
    if (isCreatingRef.current) {
      return
    }

    if (!formikRef.current) {
      return
    }

    const errors = await formikRef.current.validateForm()

    if (Object.keys(errors || {}).length > 0) {
      context.service.toast.error(t('PLEASE_FIX_VALIDATION_ERRORS'))
      return
    }

    // Check if document is selected after validation
    if (!values.fsi?.id) {
      context.service.toast.error(t('PLEASE_SELECT_DOCUMENT'))
      return
    }

    isCreatingRef.current = true
    const typeData = values.typeData
    // Build request object matching legacy expectations
    const requestToSend: any = {
      initialApprovers: null,
      approvers: null,
      fileName: values.fileName,
      description: values.description,
      fileIds: [values.fsi.id],
      fileVersionId: selectedVersion,
      parentFolderId: parentFsi?.id,
      workflowType: WorkflowType.CREATE_REFERENCE
    }

    // Assemble approvers if needed
    if (typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW') {
      requestToSend.initialApprovers =
        context.api.files.assembleFsiCRApproversForServer(typeData).initialApprovers.steps
    } else if (typeData?.initialApprovers?.type === 'USE_PREDEFINED') {
      requestToSend.initialApprovers =
        context.api.files.assembleFsiCRApproversForServer(typeData).initialApprovers.steps
    }
    if (typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW') {
      requestToSend.approvers = context.api.files.assembleFsiApproversForServer(typeData).approvers.steps
    }

    const { requestDocumentChange } = await import('../../../../services/api/workflows')
    const { getById } = await import('../../../../services/api/workflows')

    requestDocumentChange(requestToSend).then(
      async (response) => {
        if (response.workflowStatus === 'FINISHED') {
          window.dispatchEvent?.(new CustomEvent('updateFiles'))
          doClose()
          isCreatingRef.current = false
          return
        }

        doClose()

        const wf = await getById(response.id)
        await workflowService.autorunTaskApprovals(wf).finally(() => {
          context.service.toast.success(t('BROWSER_ADD_FILE_SUCCESS_MSG', { workflowNo: wf.workflowNo }))
          window.dispatchEvent?.(new CustomEvent('updateFiles'))
          isCreatingRef.current = false
        })
      },
      (error) => {
        context.service.toast.error(error?.body?.message || error?.message)
        isCreatingRef.current = false
      }
    )
  }

  const doClose = () => {
    setIsOpen(() => false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      size="lg"
      className="add-alias-modal-wrapper theme-sandman"
      title={t('BROWSER_ADD_ALIAS_H_TITLE')}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={save}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          return (
            <>
              <Modal.Body>
                <div className="fs12-imp">
                  <div>
                    <div className="form-group">
                      <h4 className="form-headline2 fsi-picker-header mb10">{t('SELECT_DESTIONATION_DOC')}</h4>
                      <FsiPicker
                        options={{
                          showRC: true,
                          showSystem: true,
                          onlyFoldersAndDocuments: true,
                          selector: 'file'
                        }}
                        onSelect={(selectedIds: (number | string)[] | TreeNode) => {
                          // FsiPicker passes TreeNode object when single file is selected
                          let treeNode: TreeNode

                          if (Array.isArray(selectedIds)) {
                            return
                          } else {
                            treeNode = selectedIds
                          }

                          if (!treeNode || !treeNode.id) return

                          // Extract only needed properties to avoid circular references (parent/children)
                          const fsi: FsiObject = {
                            id: Number(treeNode.id),
                            name: treeNode.name,
                            type: treeNode.type,
                            fileName: treeNode.fileName,
                            systemFolderType: treeNode.systemFolderType
                          }

                          // Use lowercase 'filename' to match old implementation
                          const fileName = t('ALIAS_FOR_FILENAME', { filename: fsi.fileName })

                          setFieldValue('fsi', fsi)
                          setFieldValue('fileName', fileName)
                          setRequest((reqData: RequestData) => ({
                            ...reqData,
                            fileName: fileName?.length ? fileName : ''
                          }))
                          setSelectedDocument(fsi)

                          documentSelectedFromComponent(fsi)
                        }}
                      />
                      <ErrorMessage name="fsi" />
                    </div>

                    <div className="form-group w50p">
                      <label>{t('NAME')}</label>
                      <div>
                        <Field
                          type="text"
                          name="fileName"
                          maxLength={234}
                          id="filenameInput"
                          style={{ width: '-webkit-fill-available' }}
                        />
                        <ErrorMessage name="fileName" />
                      </div>
                    </div>

                    {selectedDocument?.id && (
                      <div>
                        <h4 className="form-headline2 mb10">{t('SELECT_DOC_VERSION_LABEL')}</h4>
                        <table className="custom-version-table mb20">
                          <thead>
                            <tr className="table-header">
                              <th className="h version">{t('VERSION')}</th>
                              <th className="h lastupdate">{t('TABLE_TH_UPDATED_ON')}</th>
                              <th className="h size">{t('SIZE')}</th>
                              <th className="h actions">{t('ACTIONS')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="table-row">
                              <td className="h version">
                                <input
                                  type="radio"
                                  name="version"
                                  id="input-versionNull"
                                  checked={!selectedVersion}
                                  onChange={() => {
                                    setSelectedVersion(null)
                                  }}
                                />
                                <label htmlFor="input-versionNull">
                                  <span className="bold">{t('STATE_EFFECTIVE')}</span>
                                </label>
                              </td>
                              <td className="h lastupdate">{t('NOT_AVAILABLE')}</td>
                              <td className="h size">{t('NOT_AVAILABLE')}</td>
                              <td className="h actions">{t('NOT_AVAILABLE')}</td>
                            </tr>
                            {versions.map((item, index) => (
                              <tr key={index} className={`table-row ${index === 0 ? 'current' : ''}`}>
                                <td className="h version">
                                  <input
                                    type="radio"
                                    name="version"
                                    id={`input-version${item.id}`}
                                    checked={selectedVersion === item.id}
                                    onChange={() => {
                                      setSelectedVersion(item.id)
                                    }}
                                  />
                                  <label htmlFor={`input-version${item.id}`}>
                                    <span className="bold">
                                      {item?.versionNo} {index === 0 ? t('(CURRENT)') : ''}
                                    </span>
                                  </label>
                                </td>
                                <td className="h lastupdate">
                                  {context.service.filter.date(item.effectiveDate, false)}
                                </td>
                                <td className="h size">{bytes(item.size)}</td>
                                <td className="h actions">
                                  <a
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      navigate(`/show-file?fileId=${selectedDocument?.id}&version=${item.id}`)
                                      doClose()
                                    }}
                                  >
                                    {t('VIEW')}
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div>
                      <label htmlFor="in-f">{t('BROWSER_SEND_RQST_REASON_LABEL')}</label>
                      <div className="form-group w50p">
                        <TextArea
                          autoFocus
                          maxLength={254}
                          name="description"
                          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                            const { value } = event.target
                            setFieldValue('description', value)
                            setRequest((reqData: RequestData) => ({ ...reqData, description: value }))
                          }}
                          id="qc-desc"
                          value={values?.description}
                          rows={6}
                        />
                        <ErrorMessage name="description" />
                      </div>
                    </div>

                    {values?.typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW' && (
                      <div>
                        <FieldArray name="typeData.initialApprovers.steps">
                          {(arrayHelpers) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.initialApprovers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth={true}
                            />
                          )}
                        </FieldArray>
                      </div>
                    )}

                    {values?.typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW' && (
                      <div>
                        <FieldArray name="typeData.approvers.steps">
                          {(arrayHelpers) => (
                            <UserGroupSteps
                              arrayHelpers={arrayHelpers}
                              values={values?.typeData?.approvers?.steps || []}
                              nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
                              labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                              increaseUserGroupWidth={true}
                            />
                          )}
                        </FieldArray>
                      </div>
                    )}
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button className="btn btn-close btn-link" type="button" onClick={doClose}>
                  {t('CANCEL')}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={isCreatingRef.current}
                >
                  {isCreatingRef.current ? t('SENDING...') : t('BROWSER_SEND_REQUEST')}
                </button>
              </Modal.Footer>
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default AddAliasModal
